/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {Internal} from '@adobe/exc-app/internal';
import type Metric from '@adobe/exc-app/metrics/Metric';

// Contains the events that should be marked.
const eventsMapping: {[key: string]: string} = {};

// Filter function
function metricsWrite<T extends Metric>(record: T): Promise<T> {
  if (record.event) {
    const events = Array.isArray(record.event) ? record.event : [record.event];
    events.forEach((event: string) => {
      if (event in eventsMapping) {
        window.performance.mark(eventsMapping[event]);
      }
    });
  }
  return Promise.resolve(record);
}

/**
 * Setup the events marker by using the filter approach documented here:
 * https://git.corp.adobe.com/exc/metrics-sdk-js/blob/master/README.md#filter
 * @param eventsToMark An array of events to be marked
 * @param transformFunc a function to transform event names used to enforce a naming convention.
 */
export function markEventsPerformance(eventsToMark: Array<string>, transformFunc = (a: string): string => a): void {
  eventsToMark.forEach(event => {
    eventsMapping[event] = transformFunc(event);
  });
  if (window.performance && typeof window.performance.mark === 'function') {
    Internal.setFilter(metricsWrite);
  }
}

export type {
  CustomKeyboardEvent,
  CustomMouseEvent,
  UserKeyEvent,
  UserMouseEvent
} from './Events';
export {Events, EventsThirdParty} from './Events';
